import React from 'react'
import map from 'lodash/map'
import { Pagination } from '@mui/material'
import EmptyTablePlaceholder from '../../shared/EmptyTablePlaceholder'
import TableCell from '../TableCell'
import StatusBadge from './StatusBadge'
import VoucherBadge from './VoucherBadge'
import { t } from '../../../i18n'
import { OrderIssueRow } from '../types'

const scope = 'orders'

const issueReasonLabel = (issue: OrderIssueRow): string => {
  if (issue.issue_type === 'failed_order') {
    const reason = issue.order_failed_reason
    if (reason) {
      return t(`issues.fail_reasons.${reason}`, {
        scope,
        defaultValue: reason.replaceAll('_', ' ')
      })
    }
    return t('issues.types.failed_order', { scope })
  }

  return t(`issues.types.${issue.issue_type}`, {
    scope,
    defaultValue: issue.issue_type.replaceAll('_', ' ')
  })
}

export interface IssueDetailsPermissions {
  canViewQueuedOrders: boolean
  canViewOrders: boolean
}

const detailsHref = (issue: OrderIssueRow, permissions: IssueDetailsPermissions): string | null => {
  if (issue.queued_order_id && permissions.canViewQueuedOrders) return `/queued_orders/${issue.queued_order_id}`
  if (issue.order_id && permissions.canViewOrders) return `/orders/${issue.order_id}`
  return null
}

interface IssueGroup {
  key: string
  issues: OrderIssueRow[]
}

const groupIssuesByMeal = (issues: OrderIssueRow[]): IssueGroup[] => {
  const groups: IssueGroup[] = []
  const groupsByMeal: Record<number, IssueGroup> = {}

  issues.forEach((issue) => {
    const mealId = issue.queued_order_id
    if (mealId === null) {
      groups.push({ key: `issue-${issue.id}`, issues: [issue] })
      return
    }

    const existing = groupsByMeal[mealId]
    if (existing) {
      existing.issues.push(issue)
    } else {
      const group = { key: `meal-${mealId}`, issues: [issue] }
      groupsByMeal[mealId] = group
      groups.push(group)
    }
  })

  return groups
}

const IssueRow = ({
  issue,
  groupSize,
  firstInGroup,
  permissions
}: {
  issue: OrderIssueRow
  groupSize: number
  firstInGroup: boolean
  permissions: IssueDetailsPermissions
}) => {
  const href = detailsHref(issue, permissions)

  return (
    <tr>
      <td className="issue-when-cell">
        {issue.recent && (
          <span
            className="issue-recent-dot"
            title={t('issues.recent', { scope })}
          />
        )}
        {issue.created_at || '-'}
      </td>
      <TableCell value={issueReasonLabel(issue)} />
      <TableCell value={issue.product_name} />
      {firstInGroup && (
        <>
          <TableCell value={issue.recipe_name} rowSpan={groupSize} />
          <TableCell
            value={issue.queued_order_id ? `#${issue.queued_order_id}` : null}
            rowSpan={groupSize}
          />
          <TableCell value={issue.order_number} rowSpan={groupSize} />
          <TableCell value={issue.external_id} rowSpan={groupSize} />
          <TableCell value={issue.phone_number} rowSpan={groupSize} />
          <td className="status-cell" rowSpan={groupSize}>
            <div className="status-cell__badges">
              <StatusBadge status={issue.queued_order_state} />
              <VoucherBadge voucher={issue.voucher} />
            </div>
          </td>
          <td rowSpan={groupSize}>
            {href && (
              <a href={href} className="btn btn-sm bw-button">
                {t('see_details', { scope })}
              </a>
            )}
          </td>
        </>
      )}
    </tr>
  )
}

const IssuesTable = ({
  issues,
  currentPage,
  pageCount,
  onPageChange,
  permissions
}: {
  issues: OrderIssueRow[]
  currentPage: string
  pageCount: number | null
  onPageChange: (event: React.ChangeEvent<unknown>, page: number) => void
  permissions: IssueDetailsPermissions
}) => {
  if (!issues.length) {
    return (
      <EmptyTablePlaceholder heading={t('issues.no_records_heading', { scope })} />
    )
  }

  return (
    <>
      <table className="table table-robis orders-table table-order-issues">
        <thead className="thead-robis">
          <tr>
            <th>{t('issues.when', { scope })}</th>
            <th>{t('issues.issue', { scope })}</th>
            <th>{t('issues.product', { scope })}</th>
            <th>{t('issues.recipe', { scope })}</th>
            <th>{t('issues.meal_id', { scope })}</th>
            <th>{t('issues.order_number', { scope })}</th>
            <th>{t('issues.external', { scope })}</th>
            <th>{t('phone_number', { scope, defaultValue: 'Phone' })}</th>
            <th>{t('issues.meal_status', { scope })}</th>
            <th></th>
          </tr>
        </thead>
        {map(groupIssuesByMeal(issues), (group: IssueGroup) => (
          <tbody key={group.key}>
            {map(group.issues, (issue: OrderIssueRow, index: number) => (
              <IssueRow
                key={issue.id}
                issue={issue}
                groupSize={group.issues.length}
                firstInGroup={index === 0}
                permissions={permissions}
              />
            ))}
          </tbody>
        ))}
      </table>
      {pageCount !== null && pageCount > 1 && (
        <Pagination
          count={pageCount}
          size="large"
          page={Math.max(parseInt(currentPage, 10) || 1, 1)}
          variant="outlined"
          shape="rounded"
          onChange={onPageChange}
        />
      )}
    </>
  )
}

export default IssuesTable
